import React from "react";
import { Link, useNavigate } from "react-router-dom";
import NavBar from "@/components/NavBar";
import BetaBanner from "@/components/BetaBanner";
import { LANDING } from "@/constants/testIds";
import { useAuth } from "@/contexts/AuthContext";
import useSEO from "@/hooks/useSEO";
import {
  ShieldCheck, Camera, MessageSquare, BadgeCheck, Lock, MapPin,
  ArrowRight, Sparkles, Heart, FileCheck2, ShoppingBag, Wrench,
  Star, Users, Globe, ShieldPlus,
} from "lucide-react";

const SERVICES = [
  {
    icon: Heart,
    title: "Parent & Family Check-ins",
    blurb: "Weekly or monthly visits with photo reports. Peace of mind, delivered.",
    accent: "from-[#F2A900]/30 to-transparent",
  },
  {
    icon: ShoppingBag,
    title: "Purchase & Delivery",
    blurb: "From birthday surprises to groceries — bought, wrapped, delivered with proof.",
    accent: "from-[#E85D4E]/30 to-transparent",
  },
  {
    icon: FileCheck2,
    title: "Document Collection",
    blurb: "Government, school, embassy. We queue, collect, and courier — you don't.",
    accent: "from-[#36A374]/30 to-transparent",
  },
  {
    icon: Camera,
    title: "Project Verification",
    blurb: "On-site progress visits with video & photo evidence. No more wondering.",
    accent: "from-[#38B6FF]/30 to-transparent",
  },
  {
    icon: Wrench,
    title: "Hire-a-Professional",
    blurb: "Vetted plumbers, electricians, lawyers — supervised end-to-end.",
    accent: "from-[#F2A900]/40 to-transparent",
  },
];

const STEPS = [
  { n: "01", title: "Post your task", body: "Tell us what you need — purchase, document pickup, family visit. Set a budget." },
  { n: "02", title: "Fund the escrow", body: "Your money is held — released only when the task is delivered to your satisfaction." },
  { n: "03", title: "Verified agent assigned", body: "Background-checked, insured, and trained in diaspora communication protocols." },
  { n: "04", title: "Three photo updates", body: "Visual proof at every milestone. No noise, no 'on my way' texts." },
  { n: "05", title: "Approve & release", body: "Photos look right? One tap to release funds. Rate your agent." },
];

const TESTIMONIALS = [
  { quote: "Surprised my wife in Abuja with an iPhone for her birthday. Ada bought it, wrapped it, delivered to her office by 3 PM. Three photos. One chat. Done.", name: "Emeka, Houston" },
  { quote: "My mum gets a visit every Sunday in Surulere. I get photos. She gets companionship. ErrandDay is family.", name: "Ngozi, London" },
  { quote: "Project verification on our Lekki build — a 12-minute video walkthrough every Friday. Caught issues my brother missed for months.", name: "Tobi, Toronto" },
];

export default function Landing() {
  useSEO({
    title: "ErrandDay — Verified errands in Nigeria for the diaspora",
    description: "Send check-ins, gifts, document collections and supervised hiring back home — built for the Nigerian diaspora. Verified agents, escrow protection, photo proof on every task.",
    canonicalPath: "/",
  });
  const nav = useNavigate();
  const { user } = useAuth();
  const isLoggedIn = !!user;
  const heroCtaText = isLoggedIn ? "Send a new errand" : "Send your first errand";
  const heroCtaHref = isLoggedIn
    ? (user.role === "client" ? "/tasks/new" : user.role === "agent" ? "/agent" : "/admin")
    : "/register";
  return (
    <div className="min-h-screen">
      <BetaBanner />
      <NavBar />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 eb-pattern opacity-40 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-20 grid md:grid-cols-12 gap-10 md:gap-14 relative">
          <div className="md:col-span-7 eb-fadein">
            <span className="eb-chip" data-testid={LANDING.navBrand}>
              <Sparkles className="w-3.5 h-3.5" /> Built for the Nigerian diaspora
            </span>
            <h1 className="mt-6 text-5xl sm:text-6xl md:text-7xl leading-[0.95]">
              Your reliable <br />
              <span className="text-[var(--eb-primary)]">hands</span> in Nigeria.
            </h1>
            <p className="mt-6 max-w-xl text-lg text-[var(--eb-text-dim)] leading-relaxed">
              From a surprise iPhone for your wife in Abuja to a Sunday visit with your mum in Surulere — verified, insured agents handle your tasks with the care, accountability, and warmth of family.
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <button
                data-testid={LANDING.heroPrimary}
                onClick={() => nav(heroCtaHref)}
                className="eb-btn eb-btn-primary"
              >
                {heroCtaText} <ArrowRight className="w-4 h-4" />
              </button>
              <button
                data-testid={LANDING.heroSecondary}
                onClick={() => nav("/register?role=agent")}
                className="eb-btn eb-btn-ghost"
              >
                Become an agent
              </button>
            </div>

            <div className="mt-10 flex flex-wrap gap-5 text-sm text-[var(--eb-text-dim)]">
              <span className="inline-flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[var(--eb-success)]" /> Up to ₦20m insured</span>
              <span className="inline-flex items-center gap-2"><Lock className="w-4 h-4 text-[var(--eb-primary)]" /> Escrow-protected</span>
              <span className="inline-flex items-center gap-2"><BadgeCheck className="w-4 h-4 text-[var(--eb-info)]" /> Identity &amp; In-Person Verified agents</span>
            </div>
          </div>

          {/* Right column: photo-timeline preview card */}
          <div className="md:col-span-5">
            <div className="eb-card p-6 md:p-7 relative overflow-hidden">
              <div className="absolute -top-12 -right-12 w-44 h-44 rounded-full bg-[var(--eb-primary)] opacity-20 blur-3xl" />
              <div className="text-xs tracking-[0.22em] uppercase text-[var(--eb-text-dim)]">Task #ER-2041 · Abuja</div>
              <div className="mt-2 font-[800] text-2xl">iPhone 15 Pro — birthday gift</div>
              <StatusBadge />

              <div className="mt-6 eb-timeline">
                <TimelineEvent
                  time="11:30 AM"
                  caption="Item purchased at authorised retailer."
                  img="https://images.unsplash.com/photo-1592899677977-9c10ca588bbd?w=600&q=80&auto=format&fit=crop"
                />
                <TimelineEvent
                  time="1:15 PM"
                  caption="Gift-wrapped with handwritten note ✍️"
                  img="https://images.unsplash.com/photo-1607344645866-009c320b63e0?w=600&q=80&auto=format&fit=crop"
                />
                <TimelineEvent
                  time="2:50 PM"
                  caption="Delivered to office reception. She'll get it at 3 PM."
                  img="https://images.unsplash.com/photo-1606144042614-b2417e99c4e3?w=600&q=80&auto=format&fit=crop"
                  last
                />
              </div>

              <div className="mt-6 flex items-center gap-3 text-xs text-[var(--eb-text-dim)]">
                <MessageSquare className="w-4 h-4 text-[var(--eb-primary)]" />
                <span>Agent Ada: <span className="text-[var(--eb-text)]">&quot;Found at ₦10,500. Proceed?&quot;</span></span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES BENTO */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-24">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <span className="eb-chip">Services</span>
            <h2 className="mt-4 text-4xl sm:text-5xl">Five tasks. Endless peace of mind.</h2>
          </div>
          <p className="max-w-md text-[var(--eb-text-dim)]">
            Hyper-focused on what matters most to the diaspora. No bloat — just the things you&apos;d ask a trusted cousin to handle.
          </p>
        </div>

        <div className="grid md:grid-cols-12 gap-5">
          {SERVICES.map((s, i) => {
            const Icon = s.icon;
            const span =
              i === 0 ? "md:col-span-7" :
              i === 1 ? "md:col-span-5" :
              i === 2 ? "md:col-span-4" :
              i === 3 ? "md:col-span-4" :
              "md:col-span-4";
            return (
              <div key={s.title} className={`eb-card p-7 relative overflow-hidden ${span} eb-rise`}>
                <div className={`absolute -top-20 -right-20 w-56 h-56 rounded-full bg-gradient-to-br ${s.accent} blur-3xl`} />
                <Icon className="w-7 h-7 text-[var(--eb-primary)] relative" />
                <h3 className="mt-4 text-2xl relative">{s.title}</h3>
                <p className="mt-3 text-[var(--eb-text-dim)] relative">{s.blurb}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="border-t border-[var(--eb-border)] bg-[var(--eb-surface)]/40">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-24">
          <div className="grid md:grid-cols-12 gap-10">
            <div className="md:col-span-4">
              <span className="eb-chip">How it works</span>
              <h2 className="mt-4 text-4xl sm:text-5xl">Five steps. Zero anxiety.</h2>
              <p className="mt-4 text-[var(--eb-text-dim)]">
                Built around the &quot;no news is good news&quot; principle. We only ping you when it matters — and always with proof.
              </p>
            </div>
            <div className="md:col-span-8 space-y-3">
              {STEPS.map((s) => (
                <div key={s.n} className="eb-elev p-6 flex items-start gap-5 hover:border-[var(--eb-primary)] transition-colors">
                  <div className="text-[var(--eb-primary)] font-[900] text-2xl tabular-nums">{s.n}</div>
                  <div>
                    <div className="font-[700] text-lg">{s.title}</div>
                    <div className="text-[var(--eb-text-dim)] mt-1">{s.body}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* TRUST SIGNALS */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-24">
        <div className="grid md:grid-cols-3 gap-5">
          <TrustBox icon={ShieldCheck} title="Up to ₦20m insured" body="Every task is backed and guaranteed by our NAICOM-licensed insurance partner — your money is safe, end to end." />
          <TrustBox icon={Users} title="Vetted agents" body="ID-verified, background-checked, trained in diaspora protocols." />
          <TrustBox icon={Lock} title="Escrow by default" body="Funds held. Released only when you approve the photos." />
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="border-t border-[var(--eb-border)]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-24">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <h2 className="text-4xl sm:text-5xl">Diaspora stories.</h2>
            <span className="eb-chip">From the family</span>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {TESTIMONIALS.map((t) => (
              <div key={t.name} className="eb-card p-7">
                <div className="flex items-center gap-1 text-[var(--eb-primary)]">
                  {["s1","s2","s3","s4","s5"].map((id) => <Star key={id} className="w-4 h-4 fill-current" />)}
                </div>
                <p className="mt-4 text-[var(--eb-text)] text-lg leading-relaxed">&quot;{t.quote}&quot;</p>
                <div className="mt-5 text-[var(--eb-text-dim)] text-sm">— {t.name}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-28">
        <div data-testid={LANDING.ctaCard} className="eb-card p-10 md:p-14 relative overflow-hidden">
          <div className="absolute inset-0 eb-pattern opacity-30" />
          <div className="relative grid md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-8">
              <h2 className="text-4xl md:text-5xl">Send your first errand tonight.</h2>
              <p className="mt-4 max-w-2xl text-[var(--eb-text-dim)]">
                We work across Lagos, Abuja, Port-Harcourt, Edo, and Delta. Sign up in 60 seconds — your first task can be live in under an hour.
              </p>
            </div>
            <div className="md:col-span-4 flex md:justify-end gap-3">
              <Link to="/register" data-testid={LANDING.getStarted} className="eb-btn eb-btn-primary">
                Get started <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-[var(--eb-border)] py-10 text-sm text-[var(--eb-text-dim)]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 flex flex-wrap justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-7 h-7 rounded bg-[var(--eb-primary)] flex items-center justify-center text-white font-black text-xs">ED</div>
            ErrandDay © 2026 — Built with love for the diaspora.
          </div>
          <div className="flex items-center gap-4">
            <span className="inline-flex items-center gap-1"><Globe className="w-4 h-4" /> NG · UK · US · CA · EU · AU</span>
          </div>
        </div>
      </footer>
    </div>
  );
}

function TimelineEvent({ time, caption, img, last }) {
  return (
    <div className={`relative ${last ? "" : "pb-5"}`}>
      <span className="eb-dot" />
      <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--eb-text-dim)]">{time}</div>
      <div className="eb-elev mt-2 p-3 flex items-center gap-3">
        <img src={img} alt="" className="w-16 h-16 rounded-md object-cover" />
        <div className="text-sm text-[var(--eb-text)]">{caption}</div>
      </div>
    </div>
  );
}

function StatusBadge() {
  return (
    <div className="mt-3 inline-flex items-center gap-2 text-xs">
      <span className="eb-status eb-status-in_progress">in progress</span>
      <span className="inline-flex items-center gap-1 text-[var(--eb-text-dim)]"><MapPin className="w-3 h-3" /> Maitama, Abuja</span>
    </div>
  );
}

function TrustBox({ icon: Icon, title, body }) {
  return (
    <div className="eb-card p-7">
      <div className="w-11 h-11 rounded-xl bg-[var(--eb-surface-2)] flex items-center justify-center">
        <Icon className="w-5 h-5 text-[var(--eb-primary)]" />
      </div>
      <h3 className="mt-5 text-2xl">{title}</h3>
      <p className="mt-2 text-[var(--eb-text-dim)]">{body}</p>
    </div>
  );
}
